/**
 * Direction of the horizontal preview glide on next/previous navigation
 *: `+1` when moving to a later sibling (slide forward), `-1` when
 * moving to an earlier one, `0` when there's nothing to compare (initial open,
 * same file, or either id missing from the current sibling list — e.g. a
 * filtered filmstrip). The `0` case lets the viewer skip the slide and let the
 * open morph play instead.
 */
export function getGlideDirection(
  prevFileId: string | undefined,
  nextFileId: string | undefined,
  siblingFiles: ReadonlyArray<{ id: string }>,
): -1 | 0 | 1 {
  if (!prevFileId || !nextFileId || prevFileId === nextFileId) return 0;
  const prevIndex = siblingFiles.findIndex((f) => f.id === prevFileId);
  const nextIndex = siblingFiles.findIndex((f) => f.id === nextFileId);
  if (prevIndex < 0 || nextIndex < 0) return 0;
  return nextIndex > prevIndex ? 1 : -1;
}

/**
 * Whether the asset detail rail offers an approval panel at all.
 *
 * The panel shows an asset's whole approval chain: the chain picker plus a
 * timeline of every step. Behind the approval-chain kill-switch it opens for
 * anyone with a chain to look at, and for anyone who could assign one — a
 * reader with neither has nothing to do there, so they keep the binary approve
 * in the header instead.
 */
export function shouldShowApprovalPanel(input: {
  /** The caller's `useApprovalChainsEnabled()`. */
  workflowEnabled: boolean;
  hasAssignedChain: boolean;
  /** `project:manage-approval-chains` — may assign or change the asset's chain. */
  canManageChain: boolean;
}): boolean {
  const { workflowEnabled, hasAssignedChain, canManageChain } = input;
  return workflowEnabled && (hasAssignedChain || canManageChain);
}
